import { gql } from "@apollo/client";

export const OUTLET_STATUS_QUERY = gql`
  query OutletStatus($id: Int!) {
    outlet(id: $id) {
      id
      channelLinks {
        id
        channelType
        channelSubType
        additionalLeadTime
        closedToday
        storeUrl
        brand {
          id
          label
        }
      }
    }
  }
`;

export const OUTLET_POS_LAYOUT_QUERY = gql`
  query OutletLayout($id: Int!) {
    outlet(id: $id) {
      id
      posLayoutId
      dynamicQrCodeChannelLinkId
      stations {
        id
        name
        posEnabled
        cfdEnabled
        cashRegisterEnabled
        seatsEnabled
        reservationsEnabled
        posPrintOnlyEnabled
        kdsEnabled
        kioskEnabled
        onlineEnabled
        dispatchEnabled
        orderListConfig
        kitchenDisplay {
          id
          name
        }
        printers {
          id
          name
        }
        paymentTerminals {
          id
          terminalId
          terminalProvider
          terminalModel
        }
      }
    }
  }
`;

export const OUTLET_FIELDS = gql`
  fragment OutletFields on Outlet {
    id
    uen
    label
    labelForPickup
    address
    postalCode
    longitude
    latitude
    contactNumber
    email
    hideContactNumberInReceipt
    customPrintedReceiptFooter
    customDigitalReceiptFooter
    sortLineItemsByPrinterTags
    divideBetweenPrinterTags
    archived
    deliveryFeeConfigId
    serviceZones {
      id
      name
      boundary
    }
    qrHash
    printers {
      id
      name
      serialNumber
      copies
      tagList
      excludeSubItemTagList
      deliveryReceiptPrinter
      customerReceiptPrinter
      kitchenReceiptPrinter
      itemReceiptPrinter
      labelPrinter
      excludeTags
      matchSubItemTags
      outletBrands {
        id
        brand {
          id
          label
        }
      }
    }
    outletBrands {
      id
      outletId
      brandId
      brand {
        id
        type
        label
        dinerBaseUrl
      }
      channelLinks {
        id
        channel {
          id
          type
          subType
          label
        }
      }
      outletBrandPrinters {
        id
        isPosLinked
        isOnlineLinked
        printer {
          id
          name
          serialNumber
          copies
          tagList
          excludeSubItemTagList
          deliveryReceiptPrinter
          customerReceiptPrinter
          kitchenReceiptPrinter
          itemReceiptPrinter
          labelPrinter
          excludeTags
          matchSubItemTags
        }
      }
      operatingHours
      outletBrandSpecialHours {
        id
        specialHours {
          id
          data
          name
          type
          startDate
          endDate
          timeRanges {
            id
            label
            secondsStart
            secondsEnd
            duration
            interval
            leadTime
            timeslotType
            fulfilmentTypes
          }
        }
      }
      outletBrandOrderValueSettings {
        id
        outletBrandId
        orderValueSetting {
          id
          orderValue
          additionalLeadTime
          valueByUnitOfTime
          unitOfTime
        }
      }
      hideModifierGroupLabelsInReceipt
      showModifierGroupLabelInItemReceipt
      grabFoodMerchantId
      grabFoodStockSyncEnabled
      grabFoodOrderSyncEnabled
      foodpandaRemoteId
      foodpandaMerchantId
      foodpandaStockSyncEnabled
      foodpandaOrderSyncEnabled
      foodpandaExcludeUnavailableItemsFromCategories
      taxRates
      taxInclusivePrices
      serviceChargeRate
      defaultTaxRates
      defaultTaxInclusivePrices
    }
    outletPaymentMethods {
      id
      paymentMethod {
        id
        type
        data
      }
    }
    outletPaymentProcessors {
      id
      paymentProcessor {
        id
        label
        storeProcessorId
      }
    }
    posPaymentTypes {
      id
      label
      subType
      paymentTypeImageUrl
      enabled
    }
    ecommercePaymentTypes {
      id
      label
      subType
      paymentTypeImageUrl
      enabled
    }
    outletPaymentTypes {
      id
      paymentType {
        id
        label
        subType
        paymentTypeImageUrl
        enabled
      }
    }
  }
`;

export const OUTLET_QUERY = gql`
  query Outlet($id: Int!) {
    outlet(id: $id) {
      ...OutletFields
    }
  }
  ${OUTLET_FIELDS}
`;

export const OUTLET_WITH_STATIONS_QUERY = gql`
  query Outlet($id: Int!) {
    outlet(id: $id) {
      ...OutletFields
      isAutoPrintEnabled
      autoPrintLeadTime
      stations {
        id
        name
        posEnabled
        cfdEnabled
        cashRegisterEnabled
        seatsEnabled
        reservationsEnabled
        posPrintOnlyEnabled
        kdsEnabled
        kioskEnabled
        onlineEnabled
        dispatchEnabled
        kitchenDisplay {
          id
          name
        }
        printerConfigs {
          id
          config
          printer {
            id
            name
            serialNumber
            copies
            tagList
            excludeSubItemTagList
            deliveryReceiptPrinter
            customerReceiptPrinter
            kitchenReceiptPrinter
            itemReceiptPrinter
            labelPrinter
            excludeTags
            matchSubItemTags
          }
        }
        kioskConfig {
          id
          channelIdentifier
          channelLinkId
          pairingKey
        }
        dispatchConfig {
          id
          tapToCompleteSingleQuantity
          tapToCompleteMode
          dispatchCallerName
          dispatchCallerInstructions
          callerTheme
          customThemeColors
          kdsChime
          showStorefrontAbbreviation
          showCustomerNameInDispatcher
          disableAnimations
          prepareAtLeadTimeMinutes
          autoClearTimeMinutes
          warningTimeType
          warningLevelsLeadTimeMinutes
          filteredFulfilmentTypes
          viewingMode
          callerCardSize
          callerPreparingSectionSize
          includeTags
          includeTagList
          excludeTags
          excludeTagList
        }
        paymentTerminalConfigs {
          id
          paymentTerminal {
            id
            outletId
            terminalId
            terminalProvider
            terminalModel
          }
        }
      }
    }
  }
  ${OUTLET_FIELDS}
`;
